import { supabase } from "@shared/lib/supabase/client";
import type { TableInsert, TableName, TableRow } from "@shared/types/query";
import {
  type UseMutationOptions,
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

export type UseCreateProps<T extends TableName> = {
  resource: T;
  mutationOptions?: Omit<
    UseMutationOptions<TableRow<T>, Error, TableInsert<T>>,
    "mutationFn"
  >;
};

export function useCreate<T extends TableName>({
  resource,
  mutationOptions,
}: UseCreateProps<T>): UseMutationResult<TableRow<T>, Error, TableInsert<T>> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values) => {
      const result = await supabase
        .from(resource)
        .insert(values as never)
        .select()
        .single();

      const { data, error } = result as {
        data: TableRow<T> | null;
        error: typeof result.error;
      };

      if (error) throw error;

      if (!data) {
        throw new Error("Failed to create record");
      }

      return data;
    },

    onSuccess: async (...args) => {
      await queryClient.invalidateQueries({
        queryKey: [resource, "list"],
      });

      await mutationOptions?.onSuccess?.(...args);
    },
    ...mutationOptions,
  });
}
